import { useCallback, useEffect, useRef, useState } from "react";
import { Animated } from "react-native";

type PlayerControlsVisibilityOptions = {
  paused: boolean;
  blocked: boolean;
  autoHideSeconds: number;
  reduceMotion: boolean;
};

/** Owns control-chrome activity, focus holds, fade timing, and teardown. */
export function usePlayerControlsVisibility({
  paused,
  blocked,
  autoHideSeconds,
  reduceMotion,
}: PlayerControlsVisibilityOptions) {
  const [visible, setVisible] = useState(true);
  const [focused, setFocused] = useState(false);
  const [activity, setActivity] = useState(0);
  const [rendered, setRendered] = useState(true);
  const opacity = useRef(new Animated.Value(1)).current;
  const lastActivityRef = useRef(0);

  const noteActivity = useCallback(() => {
    setVisible(true);
    const now = Date.now();
    if (now - lastActivityRef.current < 200) return;
    lastActivityRef.current = now;
    setActivity((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!visible || paused || blocked) return;
    const timeout = setTimeout(
      () => {
        setFocused(false);
        setVisible(false);
      },
      Math.max(2, autoHideSeconds) * 1000,
    );
    return () => clearTimeout(timeout);
  }, [activity, autoHideSeconds, blocked, focused, paused, visible]);

  useEffect(() => {
    opacity.stopAnimation();
    if (visible) {
      setRendered(true);
      Animated.timing(opacity, {
        toValue: 1,
        duration: reduceMotion ? 0 : 300,
        useNativeDriver: true,
      }).start();
      return;
    }
    Animated.timing(opacity, {
      toValue: 0,
      duration: reduceMotion ? 0 : 300,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) setRendered(false);
    });
  }, [opacity, reduceMotion, visible]);

  useEffect(() => () => opacity.stopAnimation(), [opacity]);

  return {
    visible,
    setVisible,
    focused,
    setFocused,
    rendered,
    opacity,
    noteActivity,
  };
}
